// ТЭП v2, разделённое отображение (решение владельца 2026-07-03):
//   mode="requirements" — ТРЕБОВАНИЯ ЗАДАНИЯ (строки реестра с требованием ТЗ/ТУ):
//     «что потребовал заказчик ↔ что фактически принято» — ядро вкладки;
//   mode="passport"     — ТЭП ОБЪЕКТА (строки только с заявленным в ПЗ значением):
//     «заявлено в ПЗ ↔ подтверждено в разделах» — внутренняя согласованность.
// Строка живёт ровно в одном блоке (есть требование → requirements, иначе passport).
// Оба режима читают один ответ GET /compliance/tep-vs-spec (кэш react-query общий).
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Send, ChevronDown, ChevronRight, FileSearch } from 'lucide-react';
import { api } from '@/api/client';
import { DocTraceSlideOver } from '@/components/DocTracePreview';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { toast } from '@/state/toast';

interface TepCheckEntry {
  pair: string;
  status: 'MATCH' | 'MISMATCH' | 'MISSING_REQUIRED' | 'NO_SOURCE' | 'AMBIGUOUS';
  comment: string;
}
interface TepSourceValue { value: string | null; evidence: string | null; }
interface TepSectionValue {
  section: string; value: string | null; evidence: string | null;
  document_id?: string | null; page?: number | null; verbatim?: string | null; // 15.A3 якорь
}
interface TepIndicator {
  name: string;
  unit: string;
  requirement: TepSourceValue;
  passport: TepSourceValue;
  sections: TepSectionValue[];
  checks: TepCheckEntry[];
  verdict: 'OK' | 'MISMATCH' | 'GAP' | 'UNCOMPARED';
}
interface TepReconcileResponse {
  project_id: string;
  has_registry?: boolean;
  indicators: TepIndicator[];
  summary: { total: number; ok: number; mismatch: number; gap: number; uncompared: number };
  note: string | null;
  run_id?: string;
  promoted?: Record<string, string>;
}

type Mode = 'requirements' | 'passport';

const MODE_META: Record<Mode, {
  title: string; subtitle: string;
  cols: [string, string];              // подписи 2-й и 3-й колонок
  verdicts: Record<TepIndicator['verdict'], { label: string; color: string; bg: string }>;
}> = {
  requirements: {
    title: 'Требования задания — выполнение в проекте',
    subtitle: 'Измеримые требования ТЗ и ТУ: что потребовано — и что фактически принято в проектной документации.',
    cols: ['Требуется по заданию', 'Фактически в документации'],
    verdicts: {
      OK:         { label: '✓ выполнено',           color: '#10B981', bg: 'rgba(16,185,129,.08)' },
      MISMATCH:   { label: '✗ отступление',          color: '#EF4444', bg: 'rgba(239,68,68,.08)' },
      GAP:        { label: '⚠ нет в документации',   color: '#B45309', bg: 'rgba(245,158,11,.10)' },
      UNCOMPARED: { label: '— вручную',              color: '#6B7280', bg: 'rgba(107,114,128,.08)' },
    },
  },
  passport: {
    title: 'ТЭП объекта — согласованность документации',
    subtitle: 'Характеристики, заявленные в ПЗ (том 1): подтверждаются ли они разделами проектной документации.',
    cols: ['Заявлено в ПЗ (том 1)', 'Подтверждено в разделах ПД'],
    verdicts: {
      OK:         { label: '✓ подтверждено',         color: '#10B981', bg: 'rgba(16,185,129,.08)' },
      MISMATCH:   { label: '✗ противоречие',          color: '#EF4444', bg: 'rgba(239,68,68,.08)' },
      GAP:        { label: '⚠ нет в документации',   color: '#B45309', bg: 'rgba(245,158,11,.10)' },
      UNCOMPARED: { label: '— не подтверждено',       color: '#6B7280', bg: 'rgba(107,114,128,.08)' },
    },
  },
};

export function TepReconcilePanel({ projectId, mode }: { projectId: string; mode: Mode }) {
  const qc = useQueryClient();
  const meta = MODE_META[mode];
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promotedLocal, setPromotedLocal] = useState<Record<string, boolean>>({});
  const [recalcing, setRecalcing] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tep-vs-spec', projectId],
    queryFn: () => api.get<TepReconcileResponse>(`/projects/${projectId}/compliance/tep-vs-spec`),
    refetchOnWindowFocus: false,
  });

  // Кэш сбрасывается только при изменении корпуса/реестра, поэтому
  // «Пересчитать» обязан слать force=true (иначе вернётся кэш).
  const recalc = async () => {
    setRecalcing(true);
    try {
      const fresh = await api.get<TepReconcileResponse>(
        `/projects/${projectId}/compliance/tep-vs-spec?force=true`);
      qc.setQueryData(['tep-vs-spec', projectId], fresh);
      toast.success('Сверка обновлена');
    } catch (e) {
      toast.error(e instanceof Error
        ? `Пересчёт не дождался ответа (${e.message}) — расчёт продолжается на сервере, обновите страницу через минуту`
        : 'Не удалось пересчитать');
    } finally {
      setRecalcing(false);
    }
  };

  const promote = async (name: string) => {
    setPromoting(name);
    try {
      await api.post(`/projects/${projectId}/compliance/tep-vs-spec/promote?indicator=${encodeURIComponent(name)}`, {});
      setPromotedLocal((p) => ({ ...p, [name]: true }));
      toast.success('Замечание создано');
      qc.invalidateQueries({ queryKey: ['readiness-summary', projectId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось создать замечание');
    } finally {
      setPromoting(null);
    }
  };

  // Реестр не сформирован: подсказка — только в верхнем блоке, нижний скрыт.
  if (data?.has_registry === false) {
    if (mode === 'passport') return null;
    return (
      <Card meta={meta}>
        <div className="text-muted text-center" style={{ padding: '28px 16px', border: '1px dashed #E5E7EB', borderRadius: 10, fontSize: 12.5, lineHeight: 1.6 }}>
          {data.note || 'Реестр показателей ещё не сформирован.'}
          <br />Кнопка «Сформировать реестр» — в блоке «Настройка сверки: реестр
          показателей объекта» ниже. После формирования проверьте состав и нажмите
          здесь «Пересчитать».
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return <Card meta={meta}><div className="text-center" style={{ padding: 32 }}><Spinner size={26} className="text-muted" /></div></Card>;
  }
  if (isError) {
    return (
      <Card meta={meta}>
        <div className="text-muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Не удалось загрузить: {error instanceof Error ? error.message : 'неизвестная ошибка'}
        </div>
      </Card>
    );
  }
  if (!data) return null;

  const rows = data.indicators.filter((i) =>
    mode === 'requirements' ? !!i.requirement?.value : !i.requirement?.value);
  const cnt = {
    ok: rows.filter((r) => r.verdict === 'OK').length,
    mismatch: rows.filter((r) => r.verdict === 'MISMATCH').length,
    gap: rows.filter((r) => r.verdict === 'GAP').length,
    uncompared: rows.filter((r) => r.verdict === 'UNCOMPARED').length,
  };

  return (
    <Card meta={meta}>
      <div className="flex items-center justify-between" style={{ marginBottom: 14, gap: 16 }}>
        <div className="text-ink tabular" style={{ fontSize: 12.5 }}>
          Из {rows.length} {mode === 'requirements' ? 'требований' : 'показателей'}:{' '}
          <span style={{ color: '#10B981', fontWeight: 600 }}>{cnt.ok}</span> {mode === 'requirements' ? 'выполнено' : 'подтверждено'},{' '}
          <span style={{ color: '#EF4444', fontWeight: 600 }}>{cnt.mismatch}</span> {mode === 'requirements' ? 'отступлений' : 'противоречий'},{' '}
          <span style={{ color: '#B45309', fontWeight: 600 }}>{cnt.gap}</span> без данных,{' '}
          <span style={{ color: '#6B7280', fontWeight: 600 }}>{cnt.uncompared}</span> {mode === 'requirements' ? 'вручную' : 'не подтверждено'}.
        </div>
        {mode === 'requirements' && (
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            disabled={recalcing}
            onClick={() => { void recalc(); }}
          >
            {recalcing ? 'Пересчёт…' : 'Пересчитать'}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 12.5, padding: '10px 0' }}>
          {mode === 'requirements'
            ? 'В реестре нет показателей с требованиями задания.'
            : 'В реестре нет показателей, заявленных только в ПЗ.'}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-card" style={{ overflow: 'hidden' }}>
          <div className="grid border-b border-border text-muted uppercase tracking-wider"
               style={{ gridTemplateColumns: '1.1fr 180px 1.3fr 140px', fontSize: 11, fontWeight: 600, padding: '10px 16px', background: '#FAFAFA' }}>
            <div>Показатель</div>
            <div>{meta.cols[0]}</div>
            <div>{meta.cols[1]}</div>
            <div>Вывод</div>
          </div>
          {rows.map((ind, i) => (
            <TepIndicatorRow
              key={i}
              ind={ind}
              mode={mode}
              promoted={!!(data.promoted?.[ind.name] || promotedLocal[ind.name])}
              promoting={promoting === ind.name}
              onPromote={() => promote(ind.name)}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function Card({ meta, children }: { meta: (typeof MODE_META)[Mode]; children: ReactNode }) {
  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24, marginBottom: 20 }}>
      <div style={{ marginBottom: 12 }}>
        <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{meta.title}</div>
        <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 700 }}>{meta.subtitle}</div>
      </div>
      {children}
    </div>
  );
}

/** Фактическое значение в документации: приоритет — разделы ПД, затем паспорт
 *  (том 1 ПЗ). Возвращает и подпись источника — заказчик видит, ГДЕ найдено. */
function factOf(ind: TepIndicator): { value: string; source: string } | null {
  const sec = ind.sections.find((x) => x.value != null && x.value !== '');
  if (sec) return { value: sec.value as string, source: sec.section };
  if (ind.passport.value != null && ind.passport.value !== '') {
    return { value: ind.passport.value, source: 'том 1, Пояснительная записка' };
  }
  return null;
}

/** Одна человеческая фраза-вывод вместо попарного технического жаргона. */
function humanSummary(ind: TepIndicator, mode: Mode): string {
  // Комментарии проверок уже человеческие (собираются бэкендом tep_v2) —
  // показываем первый как резюме строки.
  const c = ind.checks?.[0]?.comment;
  if (c) return c;
  if (mode === 'requirements') {
    if (ind.verdict === 'GAP') return 'Значение в проверенных материалах не найдено — уточните у проектировщика.';
    return 'Данных для сверки недостаточно.';
  }
  return ind.verdict === 'OK' ? 'Противоречий не выявлено.' : 'Данных для сверки недостаточно.';
}

function TepIndicatorRow({ ind, mode, promoted, promoting, onPromote }: {
  ind: TepIndicator; mode: Mode; promoted: boolean; promoting: boolean; onPromote: () => void;
}) {
  const [details, setDetails] = useState(false);
  const [traceSec, setTraceSec] = useState<TepSectionValue | null>(null); // 15.A3 slide-over
  const v = MODE_META[mode].verdicts[ind.verdict];
  const fact = factOf(ind);
  // Вторая колонка: требование (requirements) либо заявленное в ПЗ (passport).
  const mid = mode === 'requirements' ? ind.requirement.value : ind.passport.value;
  const midHint = mode === 'requirements' ? ind.requirement.evidence : null; // «ТЗ п. 5.3»
  // Третья колонка: для passport-режима факт из ПЗ не показываем повторно.
  const factForMode = mode === 'passport'
    ? (ind.sections.find((x) => x.value != null && x.value !== '')
        ? { value: ind.sections[0].value as string, source: ind.sections[0].section }
        : null)
    : fact;
  return (
    <div className="border-b border-border">
      <div className="grid" style={{ gridTemplateColumns: '1.1fr 180px 1.3fr 140px', padding: '12px 16px', alignItems: 'start', gap: 12 }}>
        <div>
          <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{ind.name}</div>
          {ind.unit && <div className="text-muted tabular" style={{ fontSize: 11, marginTop: 2 }}>ед.: {ind.unit}</div>}
        </div>
        <div className="text-ink tabular" style={{ fontSize: 12.5, lineHeight: 1.4 }}>
          {mid ?? <span className="text-muted">—</span>}
          {midHint && <div className="text-muted" style={{ fontSize: 10.5, marginTop: 2 }}>{midHint}</div>}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {factForMode ? (
            <>
              <span className="text-ink tabular" style={{ fontWeight: 600 }}>{factForMode.value}</span>
              <span className="text-muted" style={{ fontSize: 11 }}> · {factForMode.source}</span>
            </>
          ) : (
            <span className="text-muted">не найдено</span>
          )}
          <div className="text-muted" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.45 }}>
            {humanSummary(ind, mode)}
          </div>
        </div>
        <div>
          <span
            className="inline-flex rounded-pill"
            style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, color: v.color, background: v.bg, whiteSpace: 'nowrap' }}
          >
            {v.label}
          </span>
          {(ind.verdict === 'GAP' || ind.verdict === 'MISMATCH') && (
            promoted ? (
              <div style={{ fontSize: 10.5, color: '#047857', marginTop: 6 }}>✓ в замечаниях</div>
            ) : (
              <button
                type="button"
                onClick={onPromote}
                disabled={promoting}
                style={{
                  marginTop: 6, padding: '4px 10px', borderRadius: 14, fontSize: 10.5, fontWeight: 600,
                  background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4, opacity: promoting ? 0.6 : 1,
                }}
              >
                <Send size={10} /> {promoting ? '…' : 'В замечания'}
              </button>
            )
          )}
        </div>
      </div>
      {(ind.checks.length > 0 || ind.sections.length > 0 || ind.passport.value != null) && (
        <div style={{ padding: '0 16px 10px' }}>
          <button type="button" onClick={() => setDetails(!details)}
            className="flex items-center text-muted"
            style={{ gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0 }}>
            {details ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            подробнее: источники и проверки
          </button>
          {details && (
            <div style={{ marginTop: 8, padding: '10px 12px', background: '#FAFAFA', borderRadius: 8, fontSize: 11.5, lineHeight: 1.5 }}>
              {mode === 'requirements' && ind.passport.value != null && (
                <div style={{ marginBottom: 6 }}>
                  <span className="text-muted">Заявлено в ПЗ (том 1):</span>{' '}
                  <span className="tabular" style={{ color: '#374151' }}>{ind.passport.value}</span>
                </div>
              )}
              {ind.sections.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <span className="text-muted">Найдено в документации:</span>{' '}
                  {ind.sections.map((sec, j) => (
                    <span key={j} style={{ color: '#374151', marginRight: 10 }}>
                      <span className="tabular">{sec.section}: {sec.value ?? '—'}</span>
                      {sec.evidence && (
                        <span style={{ fontStyle: 'italic', color: '#6B7280' }}>
                          {' '}— «{sec.evidence.slice(0, 160)}{sec.evidence.length > 160 ? '…' : ''}»
                        </span>
                      )}
                      {sec.document_id && (
                        <button type="button" onClick={() => setTraceSec(sec)}
                          title="Открыть лист документа с подсветкой значения"
                          style={{
                            marginLeft: 8, padding: '2px 8px', fontSize: 11, color: '#6B7280',
                            background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 12,
                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle',
                          }}
                        >
                          <FileSearch size={11} /> в документе
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {ind.checks.length > 0 && (
                <div className="text-muted">
                  {ind.checks.map((c, k) => (
                    <div key={k}>· {c.pair}: {c.comment}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {traceSec && (
        <DocTraceSlideOver
          title={ind.name}
          subtitle={traceSec.section}
          bodyText={traceSec.value ? `Значение в документации: ${traceSec.value}` : null}
          quote={traceSec.evidence}
          // evidence — ТОЧНАЯ цитата значения (напр. «…включено 24 стрелки»);
          // verbatim — весь чанк (широкий абзац) → бокс мимо значения. Ищем по
          // evidence, verbatim — только запасной, если цитаты нет.
          searchQuote={traceSec.evidence || traceSec.verbatim}
          documentId={traceSec.document_id}
          // Лист чанка (traceSec.page) НЕНАДЁЖЕН (часто = титульник тома) → ищем
          // doc-wide по тексту значения, как у норм/решений: поиск сам находит лист.
          page={null}
          onClose={() => setTraceSec(null)}
        />
      )}
    </div>
  );
}
